<template>
  <div v-if="filters.length > 0" class="active-filter-chips">
    <div class="chips-container">
      <div
        v-for="filter in filters"
        :key="filter.id"
        class="filter-chip"
        :class="chipClass(filter)"
        @click="$emit('click-chip', filter.id)"
      >
        <span class="chip-dot" :class="dotClass(filter)"></span>
        <span class="chip-text">{{ chipLabel(filter) }}</span>
        <button class="chip-remove" @click.stop="$emit('remove', filter)" title="Remove filter">
          <i class="fa fa-times"></i>
        </button>
      </div>
    </div>
    <a v-if="filters.length >= 2" href="#" class="clear-all-link" @click.prevent="$emit('clear-all')">
      Clear All Filters
    </a>
  </div>
</template>

<script>
export default {
  name: 'ActiveFilterChips',
  props: {
    filters: {
      type: Array,
      required: true
    }
  },
  methods: {
    chipClass(filter) {
      if (filter.filterType === 'procedure') return 'chip-procedure';
      if (filter.filterType === 'insurance') return 'chip-insurance';
      if (filter.filterType === 'demographic') return 'chip-demographic';
      if (filter.filterType === 'recall') return 'chip-recall';
      return 'chip-appointment';
    },
    dotClass(filter) {
      if (filter.filterType === 'procedure') return 'dot-procedure';
      if (filter.filterType === 'insurance') return 'dot-insurance';
      if (filter.filterType === 'demographic') return 'dot-demographic';
      if (filter.filterType === 'recall') return 'dot-recall';
      return 'dot-appointment';
    },
    chipLabel(filter) {
      if (filter.filterType === 'procedure') {
        return this.procedureChipLabel(filter);
      }
      if (filter.filterType === 'appointment') {
        return this.appointmentChipLabel(filter);
      }
      if (filter.filterType === 'insurance') {
        return this.insuranceChipLabel(filter);
      }
      if (filter.filterType === 'demographic') {
        return this.demographicChipLabel(filter);
      }
      if (filter.filterType === 'recall') {
        return this.recallChipLabel(filter);
      }
      return 'Filter';
    },
    procedureChipLabel(f) {
      const parts = [];

      // Mode indicator
      parts.push(f.mode === 'none' ? 'NOT Had' : 'Has Had');

      // Proc codes or "1+ procs"
      if (f.selectionMode === 'specific' && f.procCodes && f.procCodes.length > 0) {
        if (f.procCodes.length <= 2) {
          parts.push(f.procCodes.join(', '));
        } else {
          parts.push(`${f.procCodes[0]}, ${f.procCodes[1]} +${f.procCodes.length - 2}`);
        }
      } else {
        parts.push('1+ procs');
      }

      // Status
      const statusMap = { 1: 'Tx Planned', 2: 'Completed', 3: 'Existing', 6: 'Referred' };
      if (f.statuses && f.statuses.length > 0) {
        parts.push(f.statuses.map(s => statusMap[s] || s).join(', '));
      }

      // Timeframe
      if (f.timeframe) {
        if (f.timeframe.mode === 'custom' && f.timeframe.from && f.timeframe.to) {
          parts.push(`${f.timeframe.from} - ${f.timeframe.to}`);
        } else if (f.timeframe.value) {
          const unitShort = { months: 'Mo', days: 'D', years: 'Yr' };
          parts.push(`${f.timeframe.value} ${unitShort[f.timeframe.unit] || f.timeframe.unit}`);
        }
      } else {
        parts.push('Ever');
      }

      // Fee
      if (f.fee && f.fee.operator) {
        const opLabel = f.fee.operator === 'gt' ? '>' : '<';
        parts.push(`Fee${opLabel}$${f.fee.amount}`);
      }

      return parts.join(' | ');
    },
    appointmentChipLabel(f) {
      const parts = [];

      // Mode
      parts.push(f.mode === 'none' ? 'No Appt' : 'Has Appt');

      // Status
      const statusMap = { 1: 'Scheduled', 2: 'Completed', 3: 'Unscheduled', 5: 'Broken' };
      if (f.statuses && f.statuses.length > 0) {
        parts.push(f.statuses.map(s => statusMap[s] || s).join(', '));
      }

      // Timeframe
      if (f.timeframe && f.timeframe.value) {
        const dir = f.timeframe.direction === 'next' ? 'Next' : 'Last';
        const unitShort = { months: 'Mo', days: 'D', years: 'Yr' };
        parts.push(`${dir} ${f.timeframe.value} ${unitShort[f.timeframe.unit] || f.timeframe.unit}`);
      }

      return parts.join(' | ');
    },
    insuranceChipLabel(f) {
      const parts = [];

      if (f.carriers && f.carriers.length > 0) {
        parts.push(`${f.carriers.length} carrier${f.carriers.length > 1 ? 's' : ''}`);
      }
      if (f.employers && f.employers.length > 0) {
        parts.push(`${f.employers.length} employer${f.employers.length > 1 ? 's' : ''}`);
      }
      if (f.groupNumbers && f.groupNumbers.length > 0) {
        parts.push(`${f.groupNumbers.length} group#${f.groupNumbers.length > 1 ? 's' : ''}`);
      }

      return parts.length > 0 ? parts.join(' | ') : 'Insurance';
    },
    demographicChipLabel(f) {
      const parts = [];

      // Age range
      if (f.ageMin !== null && f.ageMin !== '' && f.ageMin !== undefined) {
        if (f.ageMax !== null && f.ageMax !== '' && f.ageMax !== undefined) {
          parts.push(`Age ${f.ageMin}-${f.ageMax}`);
        } else {
          parts.push(`Age ${f.ageMin}+`);
        }
      }

      // Gender
      if (f.gender && f.gender !== 'all') {
        const genderMap = { male: 'Male', female: 'Female', unknown: 'Unknown' };
        parts.push(genderMap[f.gender] || f.gender);
      }

      // Zip codes
      if (f.zipCodes && f.zipCodes.length > 0) {
        if (f.zipCodes.length <= 2) {
          parts.push(f.zipCodes.join(', '));
        } else {
          parts.push(`${f.zipCodes[0]}, ${f.zipCodes[1]} +${f.zipCodes.length - 2}`);
        }
      }

      return parts.length > 0 ? parts.join(' | ') : 'Demographic';
    },
    recallChipLabel(f) {
      const parts = ['Recall Due'];
      if (f.timeframe) {
        if (f.timeframe.mode === 'custom' && f.timeframe.from && f.timeframe.to) {
          parts.push(`${f.timeframe.from} - ${f.timeframe.to}`);
        } else if (f.timeframe.value) {
          const unitShort = { months: 'Mo', days: 'D', years: 'Yr' };
          parts.push(`Last ${f.timeframe.value} ${unitShort[f.timeframe.unit] || f.timeframe.unit}`);
        }
      }
      if (f.recallTypes && f.recallTypes.length > 0) {
        parts.push(`${f.recallTypes.length} recall type${f.recallTypes.length > 1 ? 's' : ''}`);
      }
      if (f.providers && f.providers.length > 0) {
        parts.push(`${f.providers.length} provider${f.providers.length > 1 ? 's' : ''}`);
      }
      return parts.join(' | ');
    }
  }
};
</script>

<style scoped lang="scss">
.active-filter-chips {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.625rem 0.75rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-bottom: 0.75rem;
}

.chips-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.5rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.75rem;
  color: #334155;
  cursor: pointer;
  transition: border-color 0.15s;

  &:hover {
    border-color: #94a3b8;
  }
}

.chip-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.dot-procedure { background: #547fff; }
.dot-appointment { background: #f59e0b; }
.dot-insurance { background: #06b6d4; }
.dot-demographic { background: #10b981; }
.dot-recall { background: #14b8a6; }

.chip-text {
  white-space: nowrap;
}

.chip-remove {
  background: none;
  border: none;
  padding: 0 0.125rem;
  color: #94a3b8;
  cursor: pointer;
  font-size: 0.625rem;
  line-height: 1;

  &:hover {
    color: #ef4444;
  }
}

.clear-all-link {
  font-size: 0.75rem;
  color: #547fff;
  text-decoration: none;
  white-space: nowrap;
  flex-shrink: 0;
  padding-top: 0.25rem;

  &:hover {
    text-decoration: underline;
  }
}
</style>
